import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Button, Box, Paper, Stack, IconButton, Grow, Snackbar
} from '@mui/material';
import {
    HelpOutlineRounded, InfoRounded, CheckCircleOutlineRounded,
    WarningAmberRounded, ErrorOutlineRounded, CloseRounded
} from '@mui/icons-material';

// ============================================================================
// boubaker 23/08/2026 - Système de messages générique BLOBTIC
// Remplace window.confirm / alert natifs par une UI moderne et animée :
//   - blobticMessage.confirm({...}) : Promise<boolean> (dialog de confirmation)
//   - blobticMessage.alert({...})   : Promise<void>   (dialog d'information)
//   - blobticMessage.success/error/warning/info(titre, message?) : toast animé
//     (ordre titre-first : compatible avec l'ancien addSuccess/addError/addWarning/addInfo)
// Montage : <BlobticMessageHost /> une seule fois dans App.tsx.
// Point d'entrée UNIQUE pour tous les messages/alertes/confirmations de l'app.
// ============================================================================

export type BlobticSeverity = 'success' | 'error' | 'warning' | 'info' | 'question';

export interface BlobticMessageOptions {
    title?: string;
    message: React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    severity?: BlobticSeverity;
}

interface SeverityStyle {
    main: string;
    gradient: string;
    Icon: React.ComponentType<{ sx?: object }>;
}

const SEVERITY_STYLES: Record<BlobticSeverity, SeverityStyle> = {
    question: { main: '#2563eb', gradient: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)', Icon: HelpOutlineRounded },
    info: { main: '#0284c7', gradient: 'linear-gradient(135deg, #38bdf8 0%, #0369a1 100%)', Icon: InfoRounded },
    success: { main: '#16a34a', gradient: 'linear-gradient(135deg, #4ade80 0%, #15803d 100%)', Icon: CheckCircleOutlineRounded },
    warning: { main: '#d97706', gradient: 'linear-gradient(135deg, #fbbf24 0%, #b45309 100%)', Icon: WarningAmberRounded },
    error: { main: '#dc2626', gradient: 'linear-gradient(135deg, #f87171 0%, #b91c1c 100%)', Icon: ErrorOutlineRounded }
};

// --- Pont impératif <-> hôte -------------------------------------------------
type PendingRequest = {
    kind: 'confirm' | 'alert';
    options: BlobticMessageOptions;
    resolve: (value: boolean) => void;
};

let hostListener: ((req: PendingRequest) => void) | null = null;
let requestQueue: PendingRequest[] = [];

function enqueue(kind: 'confirm' | 'alert', options: BlobticMessageOptions): Promise<boolean> {
    return new Promise<boolean>(resolve => {
        const req: PendingRequest = { kind, options, resolve };
        if (hostListener) hostListener(req);
        else resolve(false); // hôte non monté
    });
}

// --- Toasts -------------------------------------------------------------------
export interface BlobticToast {
    id: number;
    title: React.ReactNode;
    message?: React.ReactNode;
    severity: BlobticSeverity;
}

let toastListener: ((toast: BlobticToast) => void) | null = null;
let toastSeq = 1;

function notify(title: React.ReactNode, severity: BlobticSeverity, message?: React.ReactNode) {
    if (toastListener) toastListener({ id: toastSeq++, title, message, severity });
}

export const blobticMessage = {
    confirm: (options: BlobticMessageOptions): Promise<boolean> => enqueue('confirm', options),
    alert: (options: BlobticMessageOptions): Promise<void> =>
        enqueue('alert', options).then(() => undefined),
    success: (title: React.ReactNode, message?: React.ReactNode) => notify(title, 'success', message),
    error: (title: React.ReactNode, message?: React.ReactNode) => notify(title, 'error', message),
    warning: (title: React.ReactNode, message?: React.ReactNode) => notify(title, 'warning', message),
    info: (title: React.ReactNode, message?: React.ReactNode) => notify(title, 'info', message)
};

// --- Hôte ----------------------------------------------------------------------
export const BlobticMessageHost: React.FC = () => {
    const [current, setCurrent] = useState<PendingRequest | null>(null);
    const [toasts, setToasts] = useState<BlobticToast[]>([]);

    useEffect(() => {
        hostListener = (req: PendingRequest) => {
            requestQueue.push(req);
            setCurrent(prev => prev ?? requestQueue.shift() ?? null);
        };
        return () => { hostListener = null; requestQueue = []; };
    }, []);

    useEffect(() => {
        toastListener = (toast: BlobticToast) => setToasts(prev => [...prev.slice(-3), toast]);
        return () => { toastListener = null; };
    }, []);

    const settle = (value: boolean) => {
        if (!current) return;
        current.resolve(value);
        const next = requestQueue.length > 0 ? requestQueue.shift()! : null;
        setCurrent(next);
    };

    return (
        <>
            <Dialog
                open={!!current}
                TransitionComponent={Grow}
                transitionDuration={280}
                onClose={() => settle(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '22px',
                        minWidth: { xs: '88vw', sm: 420 },
                        maxWidth: 460,
                        overflow: 'visible',
                        boxShadow: '0 24px 70px -12px rgba(15, 23, 42, 0.35)'
                    }
                }}
                slotProps={{
                    backdrop: { sx: { backgroundColor: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(5px)' } }
                }}
            >
                {current && (() => {
                    const sev = current.options.severity
                        || (current.kind === 'confirm' ? 'question' : 'info');
                    const style = SEVERITY_STYLES[sev];
                    const StyleIcon = style.Icon;
                    return (
                        <Box sx={{ position: 'relative', pt: 0 }}>
                            {/* boubaker 25/08/2026 - Barre animée indeterminate sur le dialog */}
                            <Box sx={{
                                position: 'relative',
                                height: 6,
                                borderRadius: '22px 22px 0 0',
                                overflow: 'hidden',
                                bgcolor: `${style.main}15`
                            }}>
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    height: '100%',
                                    width: '40%',
                                    borderRadius: 'inherit',
                                    background: `linear-gradient(90deg, transparent, ${style.main}, transparent)`,
                                    animation: 'bmsg-dialog-indeterminate 2.2s ease-in-out infinite',
                                    '@keyframes bmsg-dialog-indeterminate': {
                                        '0%': { left: '-40%', right: '100%' },
                                        '60%': { left: '100%', right: '-40%' },
                                        '100%': { left: '100%', right: '-40%' }
                                    }
                                }} />
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    height: '100%',
                                    width: '30%',
                                    borderRadius: 'inherit',
                                    background: `linear-gradient(90deg, transparent, ${style.main}aa, transparent)`,
                                    animation: 'bmsg-dialog-indeterminate2 2.2s ease-in-out infinite',
                                    '@keyframes bmsg-dialog-indeterminate2': {
                                        '0%': { left: '-200%', right: '100%' },
                                        '60%': { left: '107%', right: '-8%' },
                                        '100%': { left: '107%', right: '-8%' }
                                    }
                                }} />
                            </Box>
                            {/* Badge icône animé, à cheval sur le bord supérieur */}
                            <Box sx={{
                                position: 'absolute',
                                top: -26, left: '50%',
                                transform: 'translateX(-50%)'
                            }}>
                                <Box sx={{
                                    width: 64, height: 64,
                                    borderRadius: '50%',
                                    background: style.gradient,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: `0 10px 28px -6px ${style.main}80`,
                                    border: '4px solid #fff',
                                    animation: 'bmsg-pop 450ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
                                    '@keyframes bmsg-pop': {
                                        '0%': { transform: 'translateX(-50%) scale(0.2)', opacity: 0 },
                                        '100%': { transform: 'translateX(-50%) scale(1)', opacity: 1 }
                                    }
                                }}>
                                    <StyleIcon sx={{ color: '#fff', fontSize: 32 }} />
                                </Box>
                            </Box>

                            <DialogTitle sx={{
                                textAlign: 'center',
                                fontWeight: 700,
                                fontSize: '1.15rem',
                                pt: 4.5,
                                pb: 0.5
                            }}>
                                {current.options.title || ''}
                            </DialogTitle>

                            <DialogContent sx={{ pb: 1 }}>
                                <DialogContentText
                                    align="center"
                                    sx={{
                                        whiteSpace: 'pre-line',
                                        animation: 'bmsg-rise 380ms ease-out both',
                                        animationDelay: '90ms',
                                        '@keyframes bmsg-rise': {
                                            from: { transform: 'translateY(12px)', opacity: 0 },
                                            to: { transform: 'translateY(0)', opacity: 1 }
                                        }
                                    }}
                                >
                                    {current.options.message}
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 2.5 }}>
                                <Stack direction="row" spacing={1.5}>
                                    {current.kind === 'confirm' && (
                                        <Button
                                            onClick={() => settle(false)}
                                            variant="outlined"
                                            color="inherit"
                                            sx={{
                                                borderRadius: 999,
                                                px: 3,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderColor: 'divider',
                                                transition: 'all .2s ease',
                                                '&:hover': { transform: 'translateY(-1px)', bgcolor: 'action.hover' }
                                            }}
                                        >
                                            {current.options.cancelText || 'Annuler'}
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => settle(true)}
                                        autoFocus
                                        variant="contained"
                                        sx={{
                                            borderRadius: 999,
                                            px: 3.5,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: style.gradient,
                                            boxShadow: `0 8px 20px -6px ${style.main}99`,
                                            transition: 'all .2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 12px 24px -6px ${style.main}bb`
                                            }
                                        }}
                                    >
                                        {current.options.confirmText || (sev === 'error' ? 'Fermer' : 'Confirmer')}
                                    </Button>
                                </Stack>
                            </DialogActions>
                        </Box>
                    );
                })()}
            </Dialog>
            <ToastStack toasts={toasts} onClose={(id) => setToasts(prev => prev.filter(t => t.id !== id))} />
        </>
    );
};

// --- Pile de toasts animés ------------------------------------------------------
const ToastStack: React.FC<{ toasts: BlobticToast[]; onClose: (id: number) => void }> = ({ toasts, onClose }) => (
    <>
        {toasts.map((toast, index) => {
            const style = SEVERITY_STYLES[toast.severity];
            const StyleIcon = style.Icon;
            return (
                <Snackbar
                    key={toast.id}
                    open
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ mt: index * 9 + 1 }}
                    TransitionComponent={Grow}
                    transitionDuration={300}
                    autoHideDuration={4500}
                    onClose={() => onClose(toast.id)}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            py: 1.1,
                            pl: 1.4,
                            pr: 0.8,
                            borderRadius: '14px',
                            borderLeft: `5px solid ${style.main}`,
                            minWidth: 320,
                            maxWidth: 420,
                            boxShadow: '0 16px 40px -12px rgba(15, 23, 42, 0.30)',
                            animation: 'bmsg-slide 320ms cubic-bezier(0.21, 1.02, 0.73, 1) both',
                            '@keyframes bmsg-slide': {
                                from: { transform: 'translateX(40px)', opacity: 0 },
                                to: { transform: 'translateX(0)', opacity: 1 }
                            }
                        }}
                    >
                        {/* boubaker 25/08/2026 - Barre animée indeterminate (style LinearProgress MUI) */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0,
                            height: 3,
                            borderRadius: '14px 14px 0 0',
                            overflow: 'hidden',
                            bgcolor: `${style.main}15`
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                height: '100%',
                                width: '40%',
                                borderRadius: 'inherit',
                                background: `linear-gradient(90deg, transparent, ${style.main}, transparent)`,
                                animation: 'bmsg-indeterminate 1.8s ease-in-out infinite',
                                '@keyframes bmsg-indeterminate': {
                                    '0%': { left: '-40%', right: '100%' },
                                    '60%': { left: '100%', right: '-40%' },
                                    '100%': { left: '100%', right: '-40%' }
                                }
                            }} />
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                height: '100%',
                                width: '30%',
                                borderRadius: 'inherit',
                                background: `linear-gradient(90deg, transparent, ${style.main}aa, transparent)`,
                                animation: 'bmsg-indeterminate2 1.8s ease-in-out infinite',
                                '@keyframes bmsg-indeterminate2': {
                                    '0%': { left: '-200%', right: '100%' },
                                    '60%': { left: '107%', right: '-8%' },
                                    '100%': { left: '107%', right: '-8%' }
                                }
                            }} />
                        </Box>
                        <Box sx={{
                            width: 34, height: 34, flexShrink: 0,
                            borderRadius: '10px',
                            background: style.gradient,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <StyleIcon sx={{ color: '#fff', fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            {toast.title && (
                                <Box sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>
                                    {toast.title}
                                </Box>
                            )}
                            {toast.message && (
                                <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', wordBreak: 'break-word' }}>
                                    {toast.message}
                                </Box>
                            )}
                        </Box>
                        <IconButton size="small" onClick={() => onClose(toast.id)} sx={{ flexShrink: 0 }}>
                            <CloseRounded sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Paper>
                </Snackbar>
            );
        })}
    </>
);

export default BlobticMessageHost;
